import React, { useEffect, useMemo, useState } from 'react'

import moment from 'moment'

import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import {
  constants,
  isHensonFundReportEnabled,
} from '@blocktickets/shared-utils'

import {
  getFanDonationRequirements,
  getHensonFundReport,
  markDonationRequirementSatisfied,
} from '../../../../utilities/api'
import {
  capitalizeString,
  EMPTY_DISPLAY_VALUE,
  formatCurrency,
  formatDateTime,
  getTimezoneDate,
} from '../../../../utilities/helpers'
import { LoadingContainer } from '../../../LoadingContainer'
import ChargeDonationModal from './ChargeDonationModal'

import './viewProfileDonations.scss'

const { ORDER_STATUS, PAYMENT_TYPE_LABEL } = constants

function DollarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2v20M15 7.5A3.5 3.5 0 0 0 8.5 9c0 2.5 2.5 3 3.5 3.5S15 14 15 15.5A3.5 3.5 0 0 1 8.5 18"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12l4 4L19 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 5v14M5 12h14"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CoverageIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 4a8 8 0 1 0 8 8"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="M12 4v4l2.5 2.5M20 4v4h-4"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function formatRequiredSubtitle(requirement, ticketsAffected) {
  const sectionKeys = ticketsAffected?.sections
    ? Object.keys(ticketsAffected.sections)
    : (requirement.triggerSections || []).map((section) =>
        String(section)
          .replace(/^sec\.?\s*/i, '')
          .trim()
      )

  if (!sectionKeys.length) {
    return 'Season package donation requirement'
  }

  const sectionList = sectionKeys.map((section) => `Sec ${section}`).join(', ')
  return `Required because account holds tickets in ${sectionList}`
}

function formatCoverageLabel(rule, requirement) {
  if (rule?.seasonLabel) {
    return `Covers ${rule.seasonLabel}`
  }
  if (requirement.packageName) {
    return rule?.carryForward !== false
      ? `Covers ${requirement.packageName}`
      : requirement.packageName
  }
  return null
}

function formatPendingDays(pendingSince) {
  if (!pendingSince) return null
  const days = moment().diff(moment(pendingSince), 'days')
  if (days <= 0) return 'Pending today'
  return `Pending ${days} day${days === 1 ? '' : 's'}`
}

function formatActivityTime(time) {
  if (!time) return ''
  const date = moment(time)
  if (!date.isValid()) return ''
  return `${date.format('MMM D, YYYY')} · ${date.format('h:mm A')}`
}

function formatActivityText(text) {
  if (!text) return null
  const match = text.match(/^(.+?)\s+(assigned\b.+)$/i)
  if (match) {
    return (
      <>
        <strong>{match[1]}</strong> {match[2]}
      </>
    )
  }
  return text
}

function DonationStatusBadge({ status }) {
  if (status === 'satisfied' || status === 'satisfied_external') {
    return (
      <Badge
        bg="none"
        className="badge-sm badge--complete view-profile-donation__status-badge"
      >
        Satisfied
      </Badge>
    )
  }
  if (status === 'pending_external') {
    return (
      <Badge
        bg="none"
        className="badge-sm view-profile-donation__status-badge view-profile-donation__status-badge--pending"
      >
        Pending
      </Badge>
    )
  }
  return (
    <Badge
      bg="none"
      className="badge-sm view-profile-donation__status-badge view-profile-donation__status-badge--pending"
    >
      Unsatisfied
    </Badge>
  )
}

function formatPaymentType(paymentOption) {
  if (paymentOption == null || paymentOption === '') return EMPTY_DISPLAY_VALUE
  return (
    PAYMENT_TYPE_LABEL[paymentOption] || capitalizeString(String(paymentOption))
  )
}

function formatHensonOrderDate(row) {
  const zonedDate = getTimezoneDate(
    row.processedAt ?? row.createdAt,
    row.timezone
  )
  return zonedDate ? formatDateTime(zonedDate) : EMPTY_DISPLAY_VALUE
}

function HensonFundSection({ rows }) {
  const total = rows.reduce((sum, row) => {
    const amount = Number(row.customFeeAmount)
    return sum + (Number.isFinite(amount) ? amount : 0)
  }, 0)

  return (
    <Card className="card card-with-border view-profile-donation view-profile-henson mb-3">
      <Card.Body>
        <div className="view-profile-donation__header">
          <h3 className="view-profile-donation__title m-0">Henson Fund</h3>
          <p className="view-profile-donation__subtitle m-0">
            Custom fee contributions for this account.
          </p>
        </div>

        <div className="view-profile-donation__stats-panel view-profile-henson__stats">
          <DonationStat label="Total" value={formatCurrency(total)} />
          <DonationStat label="Contributions" value={String(rows.length)} />
        </div>

        <div className="table-container view-profile-henson__table">
          <Table responsive>
            <thead>
              <tr className="with-inside-borders">
                <th className="text-left">Order ID</th>
                <th className="text-left">Order Date</th>
                <th className="text-left">Payment Type</th>
                <th className="text-left">Custom Fee</th>
                <th className="text-left">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={`${row.orderId}-${row.customFeeName}-${index}`}
                  className="with-inside-borders"
                >
                  <td className="text-left">
                    {row.orderId || EMPTY_DISPLAY_VALUE}
                  </td>
                  <td className="text-left">{formatHensonOrderDate(row)}</td>
                  <td className="text-left">
                    {formatPaymentType(row.paymentOption)}
                  </td>
                  <td className="text-left">
                    {row.customFeeName || EMPTY_DISPLAY_VALUE}
                  </td>
                  <td className="text-left">
                    {row.customFeeAmount != null && row.customFeeAmount !== ''
                      ? formatCurrency(row.customFeeAmount)
                      : EMPTY_DISPLAY_VALUE}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  )
}

function DonationStat({ label, value, subtext, mutedValue = false }) {
  return (
    <div className="view-profile-donation__stat">
      <span className="view-profile-donation__stat-label">{label}</span>
      <span
        className={`view-profile-donation__stat-value ${
          mutedValue ? 'view-profile-donation__stat-value--muted' : ''
        }`}
      >
        {value}
      </span>
      {subtext && (
        <span className="view-profile-donation__stat-subtext">{subtext}</span>
      )}
    </div>
  )
}

function DonationRequirementCard({ requirement, onUpdated, onChargeDonation }) {
  const [note, setNote] = useState(requirement.note || '')
  const [isSaving, setIsSaving] = useState(false)
  const [showNoteField, setShowNoteField] = useState(Boolean(requirement.note))

  const rule = requirement.donation_rule || {}
  const ruleName = rule.name || 'Donation'
  const ticketsAffected = requirement.ticketsAffected || {}
  const canAct =
    requirement.status === 'pending_external' ||
    requirement.status === 'unsatisfied'
  const coverageLabel = formatCoverageLabel(rule, requirement)
  const chargeAmount =
    Number(requirement.minimumAmount) || Number(rule.minimumAmount) || 0

  useEffect(() => {
    setNote(requirement.note || '')
    setShowNoteField(Boolean(requirement.note))
  }, [requirement.id, requirement.note])

  const recordedSubtext = useMemo(() => {
    if (requirement.status === 'pending_external') {
      return formatPendingDays(requirement.pendingSince)
    }
    if (requirement.satisfiedAt) {
      return `Satisfied ${moment(requirement.satisfiedAt).format('MMM D, YYYY')}`
    }
    if (requirement.recordedAmount == null) {
      return null
    }
    return 'Not yet satisfied'
  }, [requirement])

  const handleRecord = () => {
    onChargeDonation?.({ ...requirement, note })
  }

  const handleMarkSatisfied = async () => {
    setIsSaving(true)
    try {
      await markDonationRequirementSatisfied({
        requirementId: requirement.id,
        amount: chargeAmount,
        note,
      })
      onUpdated?.()
    } catch (error) {
      console.error(error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card className="card card-with-border view-profile-donation mb-3">
      <Card.Body>
        <Stack
          direction="horizontal"
          className="view-profile-donation__header justify-content-between align-items-start"
        >
          <div className="view-profile-donation__header-main">
            <h3 className="view-profile-donation__title m-0">
              {requirement.requirementYear} {ruleName}
            </h3>
            <p className="view-profile-donation__subtitle m-0">
              {formatRequiredSubtitle(requirement, ticketsAffected)}
            </p>
            {coverageLabel && (
              <span className="view-profile-donation__coverage">
                <CoverageIcon />
                {coverageLabel}
              </span>
            )}
          </div>
          <DonationStatusBadge status={requirement.status} />
        </Stack>

        <div className="view-profile-donation__stats-panel">
          <DonationStat
            label="Minimum required"
            value={formatCurrency(requirement.minimumAmount)}
          />
          <DonationStat
            label="Recorded"
            value={
              requirement.recordedAmount != null
                ? formatCurrency(requirement.recordedAmount)
                : '—'
            }
            subtext={recordedSubtext}
            mutedValue={requirement.recordedAmount == null}
          />
          <DonationStat
            label="Tickets affected"
            value={
              ticketsAffected.count ? `${ticketsAffected.count} seats` : '—'
            }
            subtext={ticketsAffected.breakdown}
          />
        </div>

        {canAct && (
          <>
            {showNoteField && (
              <Form.Group className="form-group view-profile-donation__note-field mb-0">
                <Form.Label className="small-label">Note</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note about this donation"
                />
              </Form.Group>
            )}

            <div className="view-profile-donation__actions">
              <div className="view-profile-donation__actions-group">
                <Button
                  type="button"
                  variant="primary"
                  disabled={isSaving}
                  className="view-profile-donation__action-btn"
                  onClick={handleRecord}
                >
                  <DollarIcon />
                  Charge donation
                </Button>
                <Button
                  type="button"
                  variant="outline-light"
                  disabled={isSaving}
                  className="view-profile-donation__action-btn"
                  onClick={handleMarkSatisfied}
                >
                  <CheckIcon />
                  Mark paid externally
                </Button>
              </div>
              <Button
                type="button"
                variant="outline-light"
                disabled={isSaving}
                className="view-profile-donation__action-btn"
                onClick={() => setShowNoteField((current) => !current)}
              >
                <PlusIcon />
                Add note
              </Button>
            </div>
          </>
        )}

        {Array.isArray(requirement.activity) &&
          requirement.activity.length > 0 && (
            <div className="view-profile-donation__activity">
              <span className="view-profile-donation__activity-title">
                Activity
              </span>
              <ul className="view-profile-donation__activity-list">
                {requirement.activity.map((item) => (
                  <li
                    key={item.id}
                    className="view-profile-donation__activity-item"
                  >
                    <span
                      className={`view-profile-donation__activity-dot ${
                        item.type === 'success'
                          ? 'view-profile-donation__activity-dot--success'
                          : ''
                      }`}
                    />
                    <div className="view-profile-donation__activity-content">
                      <p className="view-profile-donation__activity-text">
                        {formatActivityText(item.text)}
                      </p>
                      <span className="view-profile-donation__activity-time">
                        {formatActivityTime(item.time)}
                      </span>
                      {item.note && (
                        <span className="view-profile-donation__activity-note">
                          {item.note}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
      </Card.Body>
    </Card>
  )
}

export default function DonationsTab({
  fanEmail,
  organizationUUID,
  fanProfile,
  organization,
}) {
  const [requirements, setRequirements] = useState([])
  const [hensonRows, setHensonRows] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [chargeTarget, setChargeTarget] = useState(null)
  const showHensonFund = isHensonFundReportEnabled(organizationUUID)

  const loadRequirements = async () => {
    if (!organizationUUID || !fanEmail) {
      setRequirements([])
      setHensonRows([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      const donationRequest = getFanDonationRequirements(
        organizationUUID,
        fanEmail
      )
      const hensonRequest = showHensonFund
        ? getHensonFundReport(
            organizationUUID,
            undefined,
            undefined,
            [ORDER_STATUS.COMPLETE, ORDER_STATUS.PLAN],
            { email: fanEmail }
          )
        : Promise.resolve(null)

      const [donationResult, hensonResult] = await Promise.allSettled([
        donationRequest,
        hensonRequest,
      ])

      if (donationResult.status === 'fulfilled') {
        setRequirements(donationResult.value?.data?.data || [])
      } else {
        console.error(donationResult.reason)
        setRequirements([])
      }

      if (!showHensonFund) {
        setHensonRows([])
      } else if (hensonResult.status === 'fulfilled') {
        const rows = hensonResult.value?.data
        setHensonRows(Array.isArray(rows) ? rows : [])
      } else {
        console.error(hensonResult.reason)
        setHensonRows([])
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadRequirements()
  }, [organizationUUID, fanEmail])

  const sortedRequirements = useMemo(
    () =>
      [...requirements].sort((a, b) => {
        if (Number(b.requirementYear) !== Number(a.requirementYear)) {
          return Number(b.requirementYear) - Number(a.requirementYear)
        }
        return Number(b.id) - Number(a.id)
      }),
    [requirements]
  )

  if (!fanEmail) {
    return (
      <div className="text-center py-5 text-muted">
        No fan email available for donation history.
      </div>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  return (
    <div id="view-profile-donations">
      {hensonRows.length > 0 && <HensonFundSection rows={hensonRows} />}
      {sortedRequirements.length === 0
        ? hensonRows.length === 0 && (
            <div className="text-center py-5 text-muted">
              No donations found for this user.
            </div>
          )
        : sortedRequirements.map((requirement) => (
            <DonationRequirementCard
              key={requirement.id}
              requirement={requirement}
              onUpdated={loadRequirements}
              onChargeDonation={setChargeTarget}
            />
          ))}

      <ChargeDonationModal
        show={Boolean(chargeTarget)}
        requirement={chargeTarget}
        fanProfile={fanProfile}
        organization={organization}
        onClose={() => setChargeTarget(null)}
        onSuccess={loadRequirements}
      />
    </div>
  )
}
